import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import MainLayouts from "../../layout/MainLayouts";
import BlogCard from "./BlogCard";
import { fetchPost, fetchPosts } from "../../api/blogClient";
import { formatBlogDate, setMetaTag } from "./blogUtils";
import "./blogContent.css";

const EMBED_HOSTS = /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com|player\.vimeo\.com)\//;

// Links in CMS content open in a new tab safely; only YouTube/Vimeo iframes survive.
// Registered only around this sanitize call so other DOMPurify users in the app are unaffected.
function articleHook(node) {
  if (node.tagName === "A" && node.getAttribute("href")?.startsWith("http")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
  if (node.tagName === "IFRAME" && !EMBED_HOSTS.test(node.getAttribute("src") || "")) {
    node.remove();
  }
}

const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 80);

/** Sanitizes the article HTML and gives each h2 an id for the table of contents. */
function prepareContent(html) {
  DOMPurify.addHook("afterSanitizeAttributes", articleHook);
  let clean;
  try {
    clean = DOMPurify.sanitize(html || "", {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "target"],
    });
  } finally {
    DOMPurify.removeHook("afterSanitizeAttributes");
  }
  const container = document.createElement("div");
  container.innerHTML = clean;
  const toc = [];
  const used = new Set();
  container.querySelectorAll("h2").forEach((h) => {
    let id = slugify(h.textContent || "") || `section-${toc.length + 1}`;
    while (used.has(id)) id += "-x";
    used.add(id);
    h.id = id;
    toc.push({ id, text: h.textContent });
  });
  return { html: container.innerHTML, toc };
}

function ShareButtons({ title }) {
  const { t } = useTranslation();
  const url = typeof window !== "undefined" ? window.location.href : "";
  const enc = encodeURIComponent;
  const links = [
    { label: "X", href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}` },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { label: "WhatsApp", href: `https://wa.me/?text=${enc(`${title} ${url}`)}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("blogPage.linkCopied"));
    } catch {
      toast.error(t("blogPage.copyFailed"));
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {links.map((l) => (
        <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
          className="px-4 py-2 rounded-full border border-gray-200 text-sm font-bold text-[#0b0f19] hover:border-primary hover:text-primary transition-colors">
          {l.label}
        </a>
      ))}
      <button onClick={copy}
        className="px-4 py-2 rounded-full border border-gray-200 text-sm font-bold text-[#0b0f19] hover:border-primary hover:text-primary transition-colors">
        {t("blogPage.copyLink")}
      </button>
    </div>
  );
}

function useSeo(post) {
  useEffect(() => {
    if (!post) return undefined;
    const seo = post.seo || {};
    const title = seo.meta_title || post.title;
    const description = seo.meta_description || post.excerpt || "";
    const image = seo.og_image || post.cover_image || "";
    const url = window.location.href;

    const previousTitle = document.title;
    document.title = `${title} | E1X Academy`;
    const restores = [
      setMetaTag("name", "description", description),
      setMetaTag("name", "robots", seo.robots || "index, follow"),
      setMetaTag("property", "og:type", "article"),
      setMetaTag("property", "og:title", title),
      setMetaTag("property", "og:description", description),
      setMetaTag("property", "og:image", image),
      setMetaTag("property", "og:url", url),
      setMetaTag("name", "twitter:card", "summary_large_image"),
    ];

    // The CMS's JSON-LD points at its own domain; re-anchor it to this page.
    let script;
    if (post.json_ld) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify({ ...post.json_ld, url, mainEntityOfPage: url });
      document.head.appendChild(script);
    }

    return () => {
      document.title = previousTitle;
      restores.forEach((restore) => restore());
      script?.remove();
    };
  }, [post]);
}

function ArticleSkeleton() {
  return (
    <div className="max-w-[860px] mx-auto px-6 py-16 animate-pulse space-y-4">
      <div className="h-4 w-32 bg-gray-200 rounded" />
      <div className="h-10 w-full bg-gray-200 rounded" />
      <div className="h-10 w-2/3 bg-gray-200 rounded" />
      <div className="h-[360px] w-full bg-gray-200 rounded-3xl mt-8" />
      {[1, 2, 3, 4].map((n) => <div key={n} className="h-4 w-full bg-gray-200 rounded" />)}
    </div>
  );
}

export default function BlogDetails() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | notFound | error

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setPost(null);
    setRelated([]);
    window.scrollTo(0, 0);

    fetchPost(slug)
      .then((data) => {
        if (cancelled) return;
        if (!data) { setState("notFound"); return; }
        setPost(data);
        setState("ready");

        const category = data.categories?.[0]?.slug;
        fetchPosts({ ...(category ? { category } : {}), per_page: 4 })
          .then(({ posts }) => !cancelled && setRelated(posts.filter((p) => p.slug !== data.slug).slice(0, 3)))
          .catch(() => {});
      })
      .catch((err) => !cancelled && setState(err?.response?.status === 404 ? "notFound" : "error"));

    return () => { cancelled = true; };
  }, [slug]);

  useSeo(post);

  const content = useMemo(() => prepareContent(post?.content), [post]);
  const geo = post?.geo || {};
  const takeaways = Array.isArray(geo.ai_key_takeaways) ? geo.ai_key_takeaways : [];
  const authorName = post?.author?.name || "E1X Academy";

  if (state === "loading") {
    return <MainLayouts><div className="pt-28"><ArticleSkeleton /></div></MainLayouts>;
  }

  if (state !== "ready") {
    return (
      <MainLayouts>
        <div className="pt-44 pb-32 px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-[#0b0f19] mb-4">
            {state === "notFound" ? t("blogPage.notFoundTitle") : t("blogPage.loadError")}
          </h1>
          {state === "notFound" && <p className="text-gray-500 text-lg mb-10">{t("blogPage.notFoundText")}</p>}
          <Link to="/blog" className="inline-flex px-8 py-4 rounded-full bg-primary hover:bg-[#b04233] text-white font-bold transition-colors">
            {t("blogPage.backToBlog")}
          </Link>
        </div>
      </MainLayouts>
    );
  }

  return (
    <MainLayouts>
      {/* Hero */}
      <header className="relative pt-32 pb-40 lg:pt-44 lg:pb-52 bg-[#0b0f19] overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-primary/20 blur-[120px]" />
        <div className="relative z-10 max-w-[1000px] mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-gray-400 hover:text-white transition-colors mb-10">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            {t("blogPage.backToBlog")}
          </Link>

          {post.categories?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {post.categories.map((c) => (
                <Link key={c.slug} to={`/blog?category=${encodeURIComponent(c.slug)}`}
                  className="px-3 py-1.5 rounded-full bg-primary/15 text-primary text-[11px] font-bold uppercase tracking-wider hover:bg-primary hover:text-white transition-colors">
                  {c.name}
                </Link>
              ))}
            </div>
          )}

          <h1 className="text-white text-4xl md:text-6xl font-black leading-[1.1] tracking-tight mb-6">{post.title}</h1>
          {post.excerpt && <p className="text-gray-400 text-lg md:text-xl font-light leading-relaxed max-w-3xl mb-8">{post.excerpt}</p>}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-400">
            <span className="flex items-center gap-2 text-white font-semibold">
              {post.author?.avatar ? (
                <img src={post.author.avatar} alt={authorName} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-xs font-black">{authorName.charAt(0)}</span>
              )}
              {authorName}
            </span>
            <span className="w-1 h-1 rounded-full bg-gray-600" />
            <span>{formatBlogDate(post.published_at, i18n.language)}</span>
            {post.reading_time_minutes && (
              <>
                <span className="w-1 h-1 rounded-full bg-gray-600" />
                <span>{t("blogPage.minRead", { count: Number(post.reading_time_minutes) })}</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Cover image overlapping the hero */}
      {post.cover_image && (
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 -mt-28 lg:-mt-40">
          <img src={post.cover_image} alt={post.title}
            className="w-full aspect-[16/9] object-cover rounded-[28px] shadow-[0_30px_60px_rgba(0,0,0,0.18)]" />
        </div>
      )}

      <div className="max-w-[1100px] mx-auto px-6 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-12 lg:gap-16">
        <article className="min-w-0">
          {(geo.ai_summary || takeaways.length > 0) && (
            <aside className="mb-12 rounded-3xl bg-gray-50 border border-gray-100 p-6 md:p-8">
              {geo.ai_summary && (
                <>
                  <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-primary mb-3">{t("blogPage.inShort")}</div>
                  <p className="text-[#0b0f19] text-lg leading-relaxed">{geo.ai_summary}</p>
                </>
              )}
              {takeaways.length > 0 && (
                <>
                  <div className={`text-[11px] font-bold tracking-[0.15em] uppercase text-primary mb-4 ${geo.ai_summary ? "mt-8" : ""}`}>
                    {t("blogPage.keyTakeaways")}
                  </div>
                  <ul className="list-none p-0 m-0 space-y-3">
                    {takeaways.map((item, i) => (
                      <li key={i} className="flex gap-3 text-gray-700 leading-relaxed">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-primary shrink-0 mt-0.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </aside>
          )}

          <div className="blog-content" dangerouslySetInnerHTML={{ __html: content.html }} />

          {post.tags?.length > 0 && (
            <div className="mt-14 pt-8 border-t border-gray-100 flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold text-gray-400 mr-2">{t("blogPage.tags")}</span>
              {post.tags.map((tag) => (
                <Link key={tag.slug} to={`/blog?tag=${encodeURIComponent(tag.slug)}`}
                  className="px-3 py-1.5 rounded-full bg-gray-100 text-sm font-semibold text-[#0b0f19] hover:bg-primary hover:text-white transition-colors">
                  #{tag.name}
                </Link>
              ))}
            </div>
          )}

          <div className="mt-8 lg:hidden">
            <div className="text-sm font-bold text-gray-400 mb-3">{t("blogPage.share")}</div>
            <ShareButtons title={post.title} />
          </div>
        </article>

        {/* Sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-32 space-y-10">
            {content.toc.length > 1 && (
              <nav>
                <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-4">{t("blogPage.onThisPage")}</div>
                <ul className="list-none p-0 m-0 space-y-3 border-l border-gray-200">
                  {content.toc.map((item) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} className="block -ml-px pl-4 border-l-2 border-transparent text-sm text-gray-500 hover:text-primary hover:border-primary transition-colors leading-snug">
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
            <div>
              <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-4">{t("blogPage.share")}</div>
              <ShareButtons title={post.title} />
            </div>
          </div>
        </aside>
      </div>

      {/* CTA */}
      <section className="max-w-[1100px] mx-auto px-6">
        <div className="rounded-[32px] bg-[#0b0f19] p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 relative overflow-hidden">
          <div className="absolute -bottom-32 -right-20 w-[360px] h-[360px] rounded-full bg-primary/25 blur-[100px]" />
          <div className="relative">
            <h2 className="text-white text-3xl md:text-4xl font-black mb-3">{t("blogPage.ctaTitle")}</h2>
            <p className="text-gray-400 text-lg max-w-xl">{t("blogPage.ctaText")}</p>
          </div>
          <Link to="/courses" className="relative shrink-0 px-8 py-4 rounded-full bg-primary hover:bg-[#b04233] text-white font-bold transition-colors">
            {t("blogPage.exploreCourses")}
          </Link>
        </div>
      </section>

      {related.length > 0 && (
        <section className="max-w-[1560px] mx-auto px-6 py-20 lg:py-28">
          <div className="flex items-end justify-between gap-6 mb-12">
            <h2 className="text-[#0b0f19] text-3xl md:text-5xl font-bold">
              {t("blogPage.keepReading")}<span className="text-primary">.</span>
            </h2>
            <Link to="/blog" className="hidden md:inline-flex font-bold text-[#0b0f19] hover:text-primary transition-colors">
              {t("blogPage.viewAll")}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {related.map((p) => <BlogCard key={p.id} post={p} />)}
          </div>
        </section>
      )}
      {related.length === 0 && <div className="pb-20" />}
    </MainLayouts>
  );
}
